import { useRef, useEffect } from 'react';

/**
 * Interactive waving Philippine flag rendered with raw WebGL.
 * - Cloth-like waving (traveling sine waves, pinned at the hoist).
 * - Follows the mouse (a soft bulge trails the cursor).
 * - Reacts to clicks (a decaying ripple bursts from the click point).
 * Purely decorative: pointer-events are disabled so it never blocks the UI.
 */

const VERT = `
attribute vec2 aPos;      // clip-space grid position (-1..1)
attribute vec2 aUv;       // texture uv (0..1)
uniform float uTime;
uniform vec2  uMouse;     // -1..1 (y up)
uniform float uMouseAmp;
uniform vec3  uImpulses[6]; // x,y (clip), age(sec); age<0 = inactive
varying vec2  vUv;
varying float vShade;
varying float vWave;

float clothWave(vec2 p, float t){
  float hoist = (p.x + 1.0) * 0.5;      // 0 at hoist(left) -> 1 at fly(right)
  float amp = 0.06 + hoist * 0.16;      // waves grow toward the free end
  float w = 0.0;
  w += sin(p.x * 3.4 - t * 2.2) * amp;
  w += sin(p.x * 6.1 + p.y * 2.0 - t * 3.1) * amp * 0.5;
  w += sin(p.y * 4.0 + t * 1.3) * amp * 0.35;
  return w;
}

void main(){
  vUv = aUv;
  float t = uTime;
  float wave = clothWave(aPos, t);

  // mouse bulge (soft gaussian following the cursor)
  float md = distance(aPos, uMouse);
  float bulge = exp(-md * md * 3.5) * uMouseAmp * 0.28;
  wave += bulge;

  // click ripples
  float ripple = 0.0;
  for(int i=0;i<6;i++){
    vec3 imp = uImpulses[i];
    if(imp.z < 0.0) continue;
    float d = distance(aPos, imp.xy);
    float age = imp.z;
    float ring = sin(d * 12.0 - age * 9.0);
    float env = exp(-d * 2.2) * exp(-age * 2.6);
    ripple += ring * env * 0.14;
  }
  wave += ripple;

  vWave = wave;
  // fake cloth shading from the wave slope
  float slope = (clothWave(aPos + vec2(0.02, 0.0), t) - wave) / 0.02;
  vShade = clamp(0.72 + slope * 0.55 + wave * 0.5 + bulge * 1.2, 0.35, 1.35);

  // subtle parallax displacement of the vertex itself for a 3D feel
  vec2 pos = aPos;
  pos.y += wave * 0.10;
  pos.x += wave * 0.03;
  gl_Position = vec4(pos, 0.0, 1.0);
}
`;

const FRAG = `
precision highp float;
uniform sampler2D uTex;
uniform float uTime;
varying vec2 vUv;
varying float vShade;
varying float vWave;

void main(){
  // distort the texture lookup with the wave so the print ripples too
  vec2 uv = vUv;
  uv.x += vWave * 0.05;
  uv.y += sin(vUv.x * 10.0 + uTime * 2.0) * 0.006 + vWave * 0.02;
  vec4 col = texture2D(uTex, uv);
  col.rgb *= vShade;                    // folds highlight / shade
  // gentle vignette so the flag stays vivid but edges settle a touch
  float vig = smoothstep(1.4, 0.15, distance(vUv, vec2(0.5)));
  col.rgb *= mix(0.86, 1.08, vig);
  gl_FragColor = vec4(col.rgb, 1.0);
}
`;

function drawFlagTexture() {
  const H = 512;
  const W = 1024; // 2:1 official ratio
  const cv = document.createElement('canvas');
  cv.width = W; cv.height = H;
  const g = cv.getContext('2d');

  // blue (top) / red (bottom) — peacetime orientation
  g.fillStyle = '#0038a8'; g.fillRect(0, 0, W, H / 2);
  g.fillStyle = '#ce1126'; g.fillRect(0, H / 2, W, H / 2);

  // white equilateral triangle at the hoist
  const tw = (H * Math.sqrt(3)) / 2;
  g.fillStyle = '#ffffff';
  g.beginPath();
  g.moveTo(0, 0); g.lineTo(0, H); g.lineTo(tw, H / 2); g.closePath();
  g.fill();

  const gold = '#fcd116';
  const cx = tw * 0.34, cy = H / 2;

  // sun with 8 double rays
  g.save();
  g.translate(cx, cy);
  g.fillStyle = gold;
  const rSun = H * 0.11, rRay = H * 0.2;
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2;
    for (const off of [-0.09, 0.09]) {
      g.beginPath();
      g.moveTo(Math.cos(a - off) * rSun, Math.sin(a - off) * rSun);
      g.lineTo(Math.cos(a) * rRay, Math.sin(a) * rRay);
      g.lineTo(Math.cos(a + off) * rSun, Math.sin(a + off) * rSun);
      g.closePath(); g.fill();
    }
  }
  g.beginPath(); g.arc(0, 0, rSun, 0, Math.PI * 2); g.fill();
  g.restore();

  // helper: 5-point star
  const star = (x, y, r) => {
    g.save(); g.translate(x, y); g.fillStyle = gold; g.beginPath();
    for (let i = 0; i < 5; i++) {
      const a = -Math.PI / 2 + (i * 2 * Math.PI) / 5;
      const a2 = a + Math.PI / 5;
      if (i === 0) g.moveTo(Math.cos(a) * r, Math.sin(a) * r);
      else g.lineTo(Math.cos(a) * r, Math.sin(a) * r);
      g.lineTo(Math.cos(a2) * r * 0.42, Math.sin(a2) * r * 0.42);
    }
    g.closePath(); g.fill(); g.restore();
  };
  const sr = H * 0.055;
  star(tw * 0.22, H * 0.16, sr);   // top corner
  star(tw * 0.22, H * 0.84, sr);   // bottom corner
  star(tw * 0.80, H * 0.5, sr);    // apex (fly)

  return cv;
}

export default function PhilippineFlagBackground({ className = '' }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const gl = canvas.getContext('webgl', { antialias: true, premultipliedAlpha: false });
    if (!gl) return; // graceful: existing gradient bg remains visible

    const compile = (type, src) => {
      const s = gl.createShader(type); gl.shaderSource(s, src); gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
        console.error('[flag] shader compile error:', gl.getShaderInfoLog(s));
      }
      return s;
    };
    const prog = gl.createProgram();
    gl.attachShader(prog, compile(gl.VERTEX_SHADER, VERT));
    gl.attachShader(prog, compile(gl.FRAGMENT_SHADER, FRAG));
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
      console.error('[flag] program link error:', gl.getProgramInfoLog(prog));
    }
    gl.useProgram(prog);

    // build grid
    const COLS = 90, ROWS = 56;
    const pos = [], uv = [], idx = [];
    for (let y = 0; y <= ROWS; y++) {
      for (let x = 0; x <= COLS; x++) {
        const u = x / COLS, v = y / ROWS;
        pos.push(u * 2 - 1, (1 - v) * 2 - 1);
        uv.push(u, v);
      }
    }
    const stride = COLS + 1;
    for (let y = 0; y < ROWS; y++) {
      for (let x = 0; x < COLS; x++) {
        const a = y * stride + x, b = a + 1, c = a + stride, d = c + 1;
        idx.push(a, b, c, b, d, c);
      }
    }
    const posBuf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, posBuf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(pos), gl.STATIC_DRAW);
    const aPos = gl.getAttribLocation(prog, 'aPos');
    gl.enableVertexAttribArray(aPos); gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

    const uvBuf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, uvBuf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(uv), gl.STATIC_DRAW);
    const aUv = gl.getAttribLocation(prog, 'aUv');
    gl.enableVertexAttribArray(aUv); gl.vertexAttribPointer(aUv, 2, gl.FLOAT, false, 0, 0);

    const idxBuf = gl.createBuffer();
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, idxBuf);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array(idx), gl.STATIC_DRAW);

    // flag texture
    const tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, drawFlagTexture());
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

    const uTime = gl.getUniformLocation(prog, 'uTime');
    const uMouse = gl.getUniformLocation(prog, 'uMouse');
    const uMouseAmp = gl.getUniformLocation(prog, 'uMouseAmp');
    const uImpulses = gl.getUniformLocation(prog, 'uImpulses');
    gl.uniform1i(gl.getUniformLocation(prog, 'uTex'), 0);

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = canvas.clientWidth, h = canvas.clientHeight;
      canvas.width = Math.max(1, Math.floor(w * dpr));
      canvas.height = Math.max(1, Math.floor(h * dpr));
      gl.viewport(0, 0, canvas.width, canvas.height);
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    // interaction state
    const mouse = { x: 0, y: 0, tx: 0, ty: 0, amp: 0, tamp: 0 };
    const impulses = []; // {x,y,t}
    const toLocal = (clientX, clientY) => {
      const r = canvas.getBoundingClientRect();
      const nx = ((clientX - r.left) / r.width) * 2 - 1;
      const ny = (1 - (clientY - r.top) / r.height) * 2 - 1;
      return { nx, ny, inside: clientX >= r.left && clientX <= r.right && clientY >= r.top && clientY <= r.bottom };
    };
    const onMove = (e) => {
      const { nx, ny, inside } = toLocal(e.clientX, e.clientY);
      if (!inside) { mouse.tamp = 0; return; }
      mouse.tx = nx; mouse.ty = ny; mouse.tamp = 1;
    };
    const onClick = (e) => {
      const { nx, ny, inside } = toLocal(e.clientX, e.clientY);
      if (!inside) return;
      impulses.push({ x: nx, y: ny, t: performance.now() / 1000 });
      if (impulses.length > 6) impulses.shift();
    };
    window.addEventListener('mousemove', onMove, { passive: true });
    window.addEventListener('click', onClick, { passive: true });

    let raf, start = performance.now();
    const impData = new Float32Array(18);
    const render = () => {
      const now = performance.now();
      const t = reduce ? 0 : (now - start) / 1000;
      // ease mouse
      mouse.x += (mouse.tx - mouse.x) * 0.08;
      mouse.y += (mouse.ty - mouse.y) * 0.08;
      mouse.amp += (mouse.tamp - mouse.amp) * 0.06;

      gl.uniform1f(uTime, t);
      gl.uniform2f(uMouse, mouse.x, mouse.y);
      gl.uniform1f(uMouseAmp, reduce ? 0 : mouse.amp);

      for (let i = 0; i < 6; i++) {
        const im = impulses[i];
        if (im) { impData[i * 3] = im.x; impData[i * 3 + 1] = im.y; impData[i * 3 + 2] = (now / 1000) - im.t; }
        else { impData[i * 3 + 2] = -1; }
      }
      gl.uniform3fv(uImpulses, impData);

      gl.clearColor(0.02, 0.03, 0.06, 1);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.drawElements(gl.TRIANGLES, idx.length, gl.UNSIGNED_SHORT, 0);
      raf = requestAnimationFrame(render);
    };
    render();

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('click', onClick);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      style={{ width: '100%', height: '100%', display: 'block', pointerEvents: 'none' }}
      aria-hidden="true"
      data-testid="hero-flag-canvas"
    />
  );
}
